/**
 * The one "Lägg till" menu (spec sections 3, 7, 8, 9 and 14).
 *
 * Rendered in exactly two places and nowhere else: the shell's AddSheet
 * behind "+ Lägg till" (the canonical surface) and the /lagg-till page that
 * the back link on every add route points at. Same cards, same copy, same
 * handlers, so no surface can offer more or less than another.
 *
 * The menu owns no pipeline: every file control hands its files to
 * /lagg-till/kvitto, which owns the one ingest path (shrink, inbox item,
 * Drive `Inkorg/` copy, reading).
 *
 * Data comes from the hooks below rather than from props on purpose: a host
 * that forgets to pass something must not be able to render a smaller menu.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BankIcon, BookIcon, CameraIcon, ImageIcon, PencilIcon, PhoneIcon, UploadIcon } from '../icons';
import { ActionCard, ActionCards } from '../components/ActionCard';
import { Button } from '../components/Button';
import { FileDrop } from '../components/FileDrop';
import { QrCard, buildCaptureUrl } from '../components/QrCard';
import { useAdvancedMode } from '../hooks/useAdvancedMode';
import { useCompany } from '../hooks/useCompany';
import { useSettings } from '../hooks/useSettings';
import { useStorageStatus } from '../hooks/useStorageStatus';
import { useToast } from '../hooks/useToast';
import { INBOX } from '../inbox/inboxText';
import { fetchNewReceipts } from '../inbox/fetchInbox';
import { useBreakpoint } from '../hooks/useMediaQuery';
import { stashFiles } from '../hooks/pendingFiles';
import { NAV } from '../strings';
import './AddMenu.css';

/**
 * A capture link is valid for a week (spec section 8). The expiry is minted
 * when the menu opens, so the code on screen is always fresh and there is
 * nothing to renew: a stale code is fixed by opening the menu again.
 */
const CAPTURE_LINK_TTL_MS = 7 * 24 * 60 * 60_000;

/** Shared by the sheet and the /lagg-till page so the promise is worded once. */
export const ADD_MENU_SUBTITLE = 'Släpp in ett kvitto, fota det med mobilen eller läs in ett kontoutdrag. Vi föreslår bokföringen – du bestämmer.';

/** Every word the menu says. Five surfaces used to carry five labels for one drop zone. */
const TEXT = {
  upload: { title: 'Ladda upp kvitto', drop: 'Dra hit en bild eller PDF', hint: 'eller klicka för att välja från datorn' },
  camera: { title: 'Fota kvittot', drop: 'Öppna kameran', hint: 'Bilden hamnar i Att göra', showQr: 'Visa QR-kod för en annan telefon', hideQr: 'Dölj QR-koden' },
  pick: { title: 'Välj en bild eller PDF', drop: 'Välj från telefonen', hint: 'Bilder och PDF' },
  phone: {
    title: 'Fota med mobilen',
    body: 'Visa en QR-kod som öppnar Kvittokameran på telefonen.',
    koofrBody: 'Öppna kameran på telefonen och rikta den mot koden. Telefonen loggar in i Koofr med samma app-lösenord som den här datorn, så koden ska bara visas för den som ska fota kvitton. Koden gäller i en vecka; öppna menyn igen för en ny.',
    done: 'Klart',
    doneHint: 'Stänger och hämtar kvittona du fotat till Att göra.',
    placeholder: 'Anslut Google Drive eller Koofr under Mer › Lagring så visas en QR-kod här som öppnar Kvittokameran på telefonen.' },
  bank: { title: 'Läs in ett kontoutdrag', body: 'CSV, TXT eller PDF från internetbanken. Vi matchar raderna mot dina kvitton och frågar om resten.' },
  manual: { title: 'Skriv in själv', body: 'Ett köp utan kvitto, en försäljning, en överföring eller egna pengar.' },
  verifikation: { body: 'Konton, debet och kredit i den manuella editorn.' },
} as const;

export interface AddMenuProps {
  /** `sheet` stacks the cards in the + dialog; `inline` uses the shared card grid. */
  variant?: 'sheet' | 'inline' | undefined;
  /** Awaited before every navigation: the sheet closes, onboarding completes. */
  onLeave?: (() => void | Promise<void>) | undefined;
  /** Disable the controls while the host is writing. */
  busy?: boolean | undefined;
}

export function AddMenu({ variant = 'inline', onLeave, busy }: AddMenuProps) {
  const navigate = useNavigate();
  const { company } = useCompany();
  const { state, providerLabel } = useStorageStatus(company?.id);
  const settings = useSettings();
  const toast = useToast();
  const { advanced } = useAdvancedMode();
  const phone = useBreakpoint() === 'mobile';
  const [expiresAt] = useState(() => Date.now() + CAPTURE_LINK_TTL_MS);
  const [qrOpen, setQrOpen] = useState(false);

  if (!company) return null;

  const folders = state?.folders ?? null;
  const provider = settings?.cloud.provider ?? 'drive';
  // The expiry is held in state, never recomputed in the render body: a code
  // photographed a moment ago must still be the code the screen is showing.
  // On Koofr the inbox "id" is a path, and this computer's own app password
  // rides along so the phone signs in by itself. That makes the QR a
  // credential; the card's text says so, and the phone strips it at once.
  const qrValue = folders && settings
    ? buildCaptureUrl({
        provider,
        inboxFolderId: folders.inbox,
        companyName: company.name,
        expiresAt,
        ...(provider === 'koofr'
          ? { koofrBaseUrl: settings.cloud.koofr.baseUrl, koofrEmail: settings.cloud.koofr.email, koofrAppPassword: settings.cloud.koofr.appPassword }
          : {}),
      })
    : null;

  const go = (to: string) => {
    void (async () => {
      await onLeave?.();
      navigate(to);
    })();
  };

  const onFiles = (files: File[]) => {
    if (!files.length) return;
    stashFiles(files);
    go('/lagg-till/kvitto');
  };

  /**
   * "Klart" after photographing: close the menu, go to Att göra and fetch what
   * the phone put in Inkorg right away rather than waiting for Hem's next pass.
   * The fetch outlives this component (the sheet closes); the toast reports it.
   */
  const done = () => {
    if (!company) return;
    const companyId = company.id;
    void (async () => {
      await onLeave?.();
      navigate('/att-gora');
      try {
        const result = await fetchNewReceipts(companyId);
        toast.show(INBOX.refreshDone(result.found, providerLabel), { tone: result.errors.length ? 'warning' : 'ok' });
      } catch (err) {
        toast.show(INBOX.refreshFailed(providerLabel), { tone: 'warning' });
        console.warn('Inbox fetch after phone capture failed', err);
      }
    })();
  };

  const qr = (
    <>
      <QrCard bare title={null} value={qrValue} {...(provider === 'koofr' ? { body: TEXT.phone.koofrBody } : {})} placeholder={<span>{TEXT.phone.placeholder}</span>} />
      {qrValue ? (
        <div className="add-menu-done">
          <Button variant="outline" size="s" disabled={busy ?? false} onClick={done}>
            {TEXT.phone.done}
          </Button>
          <span className="text-small text-2">{TEXT.phone.doneHint}</span>
        </div>
      ) : null}
    </>
  );

  return (
    <div className={`add-menu add-menu-${variant}`}>
      <ActionCards>
        {phone ? (
          <>
            {/* A QR cannot be scanned by the screen showing it, so on a phone
                this card opens that phone's own camera; the code stays one tap
                away for pairing a second device. */}
            <ActionCard icon={CameraIcon} title={TEXT.camera.title}>
              <FileDrop compact multiple={false} capture="environment" icon={CameraIcon} disabled={busy ?? false} onFiles={onFiles} label={TEXT.camera.drop} hint={TEXT.camera.hint} />
              <Button size="s" variant="ghost" icon={PhoneIcon} aria-expanded={qrOpen} onClick={() => setQrOpen((v) => !v)}>
                {qrOpen ? TEXT.camera.hideQr : TEXT.camera.showQr}
              </Button>
              {qrOpen ? qr : null}
            </ActionCard>
            <ActionCard icon={ImageIcon} title={TEXT.pick.title}>
              <FileDrop compact disabled={busy ?? false} onFiles={onFiles} label={TEXT.pick.drop} hint={TEXT.pick.hint} />
            </ActionCard>
          </>
        ) : (
          <>
            <ActionCard icon={UploadIcon} title={TEXT.upload.title}>
              <FileDrop compact disabled={busy ?? false} onFiles={onFiles} label={TEXT.upload.drop} hint={TEXT.upload.hint} />
            </ActionCard>
            {/* Folded away by default: the code is three times the height of
                the other cards, which made this one option look like the
                screen's main event. */}
            <ActionCard icon={PhoneIcon} title={TEXT.phone.title} body={TEXT.phone.body} expanded={qrOpen} onToggle={() => setQrOpen((v) => !v)}>
              {qr}
            </ActionCard>
          </>
        )}

        <ActionCard icon={BankIcon} title={TEXT.bank.title} body={TEXT.bank.body} onClick={() => go('/lagg-till/kontoutdrag')} />
        <ActionCard icon={PencilIcon} title={TEXT.manual.title} body={TEXT.manual.body} onClick={() => go('/lagg-till/handelse')} />
        {advanced ? <ActionCard icon={BookIcon} title={NAV.newVerifikation} body={TEXT.verifikation.body} onClick={() => go('/lagg-till/verifikation')} /> : null}
      </ActionCards>
    </div>
  );
}
